import {
  Button,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  OutlinedInput,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { LoadingButton } from "@mui/lab";
import { MuiTelInput, matchIsValidTel } from "mui-tel-input";
import PostHooks from "../../../Api/PostHooks";
import AlertComponent from "../../../Components/AlertComponent";
import ResidentialDropdowns from "../../../Components/ResidentialDropdowns";

// Changed to accept user, onClose, and onUpdated props for better reusability
const UpdateUser = ({ user, onClose, onUpdated }) => {
  // Setting defaultValues from the user prop to pre-populate the form
  const methods = useForm({
    defaultValues: {
      first_name: user?.first_name || "",
      surname: user?.surname || "",
      other_names: user?.other_names || "",
      identification_number: user?.id_number || "",
      msisdn: user?.msisdn ? `+${user.msisdn}` : "+254",
      county: user?.county || "",
      subcounty: user?.subcounty || "",
      ward: user?.ward || "",
      area: user?.village || "",
    },
  });
  const {
    watch,
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = methods;

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const msisdn = watch("msisdn");

  const [loading, response, request] = PostHooks();

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const onSubmit = async (e) => {
    try {
      if (msisdn && msisdn.length < 13) {
        setSnackbar({
          open: true,
          severity: "error",
          message: "Input a valid phone number!",
        });
      } else {
        // Payload optimized for update. It now includes the user's existing data
        // and only updates fields from the form.
        let formData = {
          ...user, // Preserve existing user data
          entity_id: user.id,
          first_name: e.first_name,
          surname: e.sirname,
          other_names: e.other_names,
          residential_address: `${e.county || ""} ${e.subcounty || ""} ${
            e.ward || ""
          } ${e.area || ""}`.trim(),
          id_number: e.identification_number,
          passport_number: e.identification_number, // Assuming it's the same
          county: e.county,
          subcounty: e.subcounty,
          ward: e.ward,
          village: e.area,
          msisdn: msisdn.replace("+", ""),
          document_number: e.identification_number,
          username: e.identification_number, // Assuming username is the ID number
          description: "User profile updated.", // More appropriate description
        };

        let props = {
          url: "/update_profile",
          body: formData,
        };

        const updResp = await request(props);

        if (
          updResp &&
          updResp.status === 200 &&
          updResp.data.StatusCode === 0
        ) {
          setSnackbar({
            open: true,
            severity: "success",
            message: "User updated successfully!",
          });

          setTimeout(() => {
            onUpdated();
            onClose();
          }, 500);
        } else {
          const errorMessage =
            updResp?.data?.StatusMessage ??
            updResp?.data?.message ??
            "An unexpected error occurred. Please try again.";

          setSnackbar({
            open: true,
            severity: "error",
            message: errorMessage,
          });
        }
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.StatusMessage ??
        error.response?.data?.message ??
        error.message ??
        "An unexpected error occurred. Please try again.";

      setSnackbar({
        open: true,
        severity: "error",
        message: errorMessage,
      });
    }
  };

  return (
    <FormProvider {...methods}>
      <AlertComponent
        open={snackbar.open}
        handleSnackbar={handleSnackbarClose}
        severity={{ severity: snackbar.severity, message: snackbar.message }}
      />

      {/* Changed id to be more specific */}
      <form onSubmit={handleSubmit(onSubmit)} id="user_update_form">
        <Grid
          sx={{
            mt: "5px",
          }}
          container
          rowSpacing={3}
          columnSpacing={{ xs: 1, sm: 2 }}
        >
          <Grid
            item
            xs={6}
            md={4}
            lg={4}
            sx={{
              paddingTop: "20px",
            }}
          >
            <FormControl fullWidth error={!!errors.first_name}>
              <InputLabel htmlFor="first_name">First name</InputLabel>
              <OutlinedInput
                id="first_name"
                {...register("first_name")}
                label="First name"
                xs={12}
                variant="outlined"
              />
              {errors.first_name && <FormHelperText>Required</FormHelperText>}
            </FormControl>
          </Grid>
          <Grid
            item
            xs={6}
            md={4}
            lg={4}
            sx={{
              paddingTop: "20px",
            }}
          >
            <FormControl fullWidth error={!!errors.sirname_name}>
              <InputLabel htmlFor="sirname_name">Sir name</InputLabel>
              <OutlinedInput
                id="sirname_name"
                {...register("sirname_name")}
                label="Sir name"
                xs={12}
                variant="filled"
              />
              {errors.sirname_name && <FormHelperText>Required</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid
            item
            xs={12}
            md={4}
            lg={4}
            sx={{
              paddingTop: "20px",
            }}
          >
            <FormControl fullWidth error={!!errors.other_names}>
              <InputLabel htmlFor="other_names">Other name</InputLabel>
              <OutlinedInput
                variant="filled"
                id="other_names"
                {...register("other_names")}
                label="Other name"
                xs={12}
              />
              {errors.other_names && <FormHelperText>Required</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid
            item
            xs={12}
            md={12}
            lg={12}
            sx={{
              paddingTop: "20px",
            }}
          >
            <FormControl fullWidth error={!!errors.identification_number}>
              <InputLabel>National ID Number/ Passport Number</InputLabel>
              <OutlinedInput
                variant="filled"
                id="identification_number"
                {...register("identification_number", {
                  required: true,
                  maxLength: 10,
                  minLength: 7,
                })}
                label="National ID Number/ Passport Number"
                xs={12}
                type="number"
                inputProps={{ step: "any", min: 0 }}

                // An admin might need to change this, but often it should be read-only.
                // For now, I'll leave it editable as per original component.
              />
              {errors.identification_number && (
                <FormHelperText>Enter a valid id number.</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid
            item
            xs={12}
            md={12}
            lg={12}
            sx={{
              paddingTop: "20px",
            }}
          >
            <Controller
              name="msisdn"
              control={control}
              rules={{
                validate: (value) => matchIsValidTel(value),
              }}
              // defaultValue is now handled by useForm defaultValues
              render={({ field, fieldState }) => (
                <MuiTelInput
                  {...field}
                  fullWidth
                  defaultCountry="KE"
                  onlyCountries={["KE"]}
                  helperText={
                    fieldState.invalid ? "Invalid mobile number!" : ""
                  }
                  error={fieldState.invalid} // Changed from fieldState.error
                  value={field.value || ""}
                  disableFormatting
                />
              )}
            />
          </Grid>

          <ResidentialDropdowns />

          <Grid
            item
            xs={12}
            sx={{
              py: "10px",
              display: "flex",
              gap: "10px",
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="outlined"
              size="large"
              type="button" // Changed from reset to button to avoid clearing form state
              onClick={onClose} // Using onClose prop
            >
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              size="large"
              type="submit"
              loading={loading}
            >
              Update
            </LoadingButton>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  );
};

export default UpdateUser;
